<script setup lang="ts">
/**
 * A section's actions by group: the `NX_FUNCTION_GROUP` entries as full-width
 * primary buttons (`NxActionList`), every other group as a labelled
 * two-column grid of compact buttons (label, then icon) below them. Set
 * `columns` to 1 for full-width buttons with wrapping labels. The
 * heading carries a chevron; with `collapsible` it toggles (`toggle`) and the
 * caller hides the body with `hideBody`.
 *
 * The kit version of the ERP's `ActionSubsectionHelper`, with the same props
 * (`itemsByGroup` is a `Map` of group name → `NxActionElement[]`, in display
 * order) and event.
 */
import { ChevronUpIcon, InfoIcon } from '@blpde/nx-icons';
import { computed } from 'vue';
import { NxButton } from '../Button';
import NxActionList from './NxActionList.vue';
import NxActionSection from './NxActionSection.vue';
import NxActionSubsection from './NxActionSubsection.vue';
import { NX_FUNCTION_GROUP, type NxActionElement } from './types';

const props = withDefaults(
  defineProps<{
    labelText: string;
    itemsByGroup: ReadonlyMap<string, readonly NxActionElement[]>;
    /** `up` while the body shows, `down` while it is folded away. */
    chevronDirection?: 'up' | 'down';
    hideHeader?: boolean;
    hideBody?: boolean;
    collapsible?: boolean;
    columns?: 1 | 2;
  }>(),
  { chevronDirection: 'up', hideHeader: false, hideBody: false, collapsible: false, columns: 2 }
);

const emit = defineEmits<{ toggle: [] }>();

const primary = computed(() => props.itemsByGroup.get(NX_FUNCTION_GROUP) ?? []);
const otherGroups = computed(() =>
  Array.from(props.itemsByGroup.entries()).filter(([name]) => name !== NX_FUNCTION_GROUP)
);
</script>

<template>
  <NxActionSection
    :label-text="props.labelText"
    :hide-header="props.hideHeader"
    :hide-body="props.hideBody"
    :collapsible="props.collapsible"
    @toggle="emit('toggle')"
  >
    <template #header>
      <ChevronUpIcon
        class="nx-action-groups__chevron nxt-icon-sm"
        :class="{ 'is-down': props.chevronDirection === 'down' }"
        :filled="true"
        aria-hidden="true"
      />
    </template>
    <NxActionList v-if="primary.length" :items="primary" />
    <NxActionSubsection
      v-for="[groupName, groupItems] in otherGroups"
      :key="`group_${groupName}`"
      :label-text="groupName"
    >
      <div class="nx-action-groups__grid" :class="{ 'is-single-column': props.columns === 1 }">
        <NxButton
          v-for="(item, idx) in groupItems"
          :key="item.id ?? idx"
          class="nx-action-groups__quick"
          height="xs"
          width="full"
          :variant="item.tone === 'danger' ? 'outline-danger' : 'outline'"
          :title="item.label"
          :disabled="item.disabled"
          :data-test="item.dataTest"
          @click="item.onClick()"
        >
          <span class="nx-action-groups__label">{{ item.label }}</span>
          <component
            :is="item.icon.component"
            v-if="item.icon"
            :filled="item.icon.filled"
            :color="item.icon.color"
            class="nxt-icon-md"
            aria-hidden="true"
          />
          <InfoIcon
            v-else
            :filled="true"
            class="nxt-icon-md"
            color="var(--nxt-fg-muted)"
            aria-hidden="true"
          />
        </NxButton>
      </div>
    </NxActionSubsection>
  </NxActionSection>
</template>

<style scoped>
.nx-action-groups__chevron {
  margin-inline-end: var(--nxt-space-2xs);
  transform-origin: center;
  transition: transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-action-groups__chevron.is-down {
  transform: rotate(-180deg);
}
.nx-action-groups__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--nxt-space-xs);
}
.nx-action-groups__quick {
  overflow: hidden;
}
.nx-action-groups__grid.is-single-column {
  grid-template-columns: minmax(0, 1fr);
}
.is-single-column .nx-action-groups__label {
  white-space: normal;
  overflow-wrap: anywhere;
}
.is-single-column .nxt-icon-md {
  flex-shrink: 0;
}
.nx-action-groups__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-align: start;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media (prefers-reduced-motion: reduce) {
  .nx-action-groups__chevron {
    transition: none;
  }
}
</style>
